import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Stable identity that invokes the callback from the latest committed render.
 *
 * Use for event handlers crossing memo/comparator boundaries, e.g. stream ticks
 * must not remint `onSendMessage` while send still reads the latest thread.
 * Prefer `useCallback` elsewhere; do not hide dependencies that should
 * retrigger effects or subscriptions.
 */
export function useStableCallback<Args extends unknown[], Result>(
  callback: (...args: Args) => Result,
): (...args: Args) => Result {
  const callbackRef = useRef(callback);

  useLayoutEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback((...args: Args) => callbackRef.current(...args), []);
}
